import { endOfDay, parseISO, startOfDay } from 'date-fns';
import {
  type TableDateRange,
  type TableDateRangeFilterConfig,
  type FilterValue,
  type TableFilterConfig,
} from '@avanta/shared/types/table';

export const filterTableData = <T, K extends FilterValue>(
  data: T[],
  filter: TableFilterConfig<T, K> | undefined,
  selectedFilterKey: K | undefined
): T[] => {
  if (!filter || selectedFilterKey === undefined) {
    return data;
  }

  const filterFn =
    filter.filterFn ?? ((row: T, key: K): boolean => row[filter.accessorKey] === key);

  return data.filter(row => filterFn(row, selectedFilterKey));
};

const getDateValue = (value: unknown): Date | undefined => {
  if (value instanceof Date) return value;
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;

  const date = typeof value === 'string' ? parseISO(value) : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export const filterTableDataByDateRange = <T>(
  data: T[],
  filter: TableDateRangeFilterConfig<T> | undefined,
  selectedDateRange: TableDateRange | undefined
): T[] => {
  if (!filter || (!selectedDateRange?.from && !selectedDateRange?.to)) {
    return data;
  }

  const filterFn =
    filter.filterFn ??
    ((row: T, dateRange: TableDateRange): boolean => {
      const value = getDateValue(row[filter.accessorKey]);
      if (!value) return false;

      if (dateRange.from && value < startOfDay(dateRange.from)) return false;
      if (dateRange.to && value > endOfDay(dateRange.to)) return false;
      return true;
    });

  return data.filter(row => filterFn(row, selectedDateRange));
};
